import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";

// Extract a hostname from a URL, safely
function hostFromUrl(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch (e) {
    return null;
  }
}

/**
 * MerchantStrip — pulls a distinct set of merchant hostnames from all deals
 * and renders their logos via Clearbit's free logo API. Falls back gracefully
 * for any hostname without a logo.
 */
export default function MerchantStrip() {
  const [hosts, setHosts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/deals");
        const seen = new Set();
        const list = [];
        for (const d of data) {
          const host = hostFromUrl(d.url);
          if (!host || seen.has(host)) continue;
          seen.add(host);
          list.push({ host, merchant: d.merchant });
          if (list.length >= 18) break;
        }
        setHosts(list);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  if (hosts.length === 0) return null;

  return (
    <section className="border-y border-black/10 bg-white overflow-hidden" data-testid="merchant-strip">
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/40 mb-4 text-center">
          Sourcing deals from 100+ merchants including
        </div>
        <div className="flex items-center justify-center flex-wrap gap-x-8 gap-y-3">
          {hosts.map(({ host, merchant }) => (
            <Link
              key={host}
              to={`/merchant/${encodeURIComponent(merchant)}`}
              className="flex items-center gap-2 grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-opacity duration-200"
              data-testid={`merchant-logo-${host}`}
              title={`View ${merchant} deals`}
            >
              <img
                src={`https://logo.clearbit.com/${host}?size=64`}
                alt={merchant}
                className="h-6 w-auto object-contain"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling.style.display = "inline"; }}
              />
              <span className="hidden font-serif font-bold text-sm text-black/60">{merchant}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
